import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import type { FirstOrderTip } from "@shared/buyerFirstRun";

// The pointing callouts on a buyer's first orders (shared/buyerFirstRun.ts picks
// which and when; the "intro" guide is a sheet, components/ordering-intro.tsx).
// Each rings the real control with a bubble beside it and never blocks it: the
// overlay takes no taps, so the buyer can tap the control itself, which also
// ends the callout. "Got it" ends it too.

export type PointingTip = Exclude<FirstOrderTip, "intro">;

export interface TipContext {
  /** "delivery", "pickup", or the one this order uses. */
  method: string;
  /** The Order Deadline countdown is on screen in the review. */
  hasDeadline: boolean;
}

const TIPS: Record<PointingTip, { anchor: string; text: (c: TipContext) => string; prefer?: "above" }> = {
  vendor: {
    anchor: '[data-testid="vendor-switcher-trigger"]',
    text: () => "Tap here to add another supplier or switch between them. Your cart is kept.",
  },
  review: {
    anchor: '[data-testid="button-submit-order"]',
    text: () => "Not sent yet. Tap Review Order when you're done.",
  },
  cart: {
    anchor: '[data-testid="label-fulfillment-day"]',
    text: (c) =>
      c.hasDeadline
        ? `Check your ${c.method} day. The Order Deadline below shows how long you have to submit or change it.`
        : `Check your ${c.method} day, then tap Submit Order.`,
    prefer: "above",
  },
  days: {
    anchor: '[data-testid="button-order-switcher-bottom"]',
    text: () => "Tap here to switch between your orders, or start a separate order for another day.",
  },
};

const GAP = 10;
const BUBBLE_MAX_W = 300;

/** The first matching element that is on screen, or null. */
function findAnchor(selector: string): HTMLElement | null {
  const vh = window.innerHeight;
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(selector))) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < vh) return el;
  }
  return null;
}

export function FirstOrderTips({
  tip,
  context,
  onDismiss,
}: {
  tip: PointingTip | null;
  context: TipContext;
  onDismiss: (tip: PointingTip) => void;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const reduceMotion = useReducedMotion();

  // Follow the anchor: it slides in (the order bar springs up), and the list
  // scrolls under it. A light poll is simpler and steadier than observers on a
  // page this busy; it stops whenever no tip is showing.
  useLayoutEffect(() => {
    if (!tip) {
      setRect(null);
      return;
    }
    let frame = 0;
    let last = "";
    const measure = () => {
      const el = findAnchor(TIPS[tip].anchor);
      const r = el?.getBoundingClientRect() ?? null;
      const key = r ? `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.width)},${Math.round(r.height)}` : "";
      if (key !== last) {
        last = key;
        setRect(r);
      }
    };
    measure();
    const id = window.setInterval(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    }, 150);
    return () => {
      window.clearInterval(id);
      cancelAnimationFrame(frame);
    };
  }, [tip]);

  // Tapping the control itself ends the tip, the same as "Got it".
  useEffect(() => {
    if (!tip) return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (target?.closest(TIPS[tip].anchor)) onDismiss(tip);
    };
    document.addEventListener("pointerdown", onPointer, true);
    return () => document.removeEventListener("pointerdown", onPointer, true);
  }, [tip, onDismiss]);

  if (typeof document === "undefined") return null;

  const show = !!tip && !!rect;
  let bubble: { left: number; top?: number; bottom?: number; arrowLeft: number; above: boolean } | null = null;
  if (show && rect) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(BUBBLE_MAX_W, vw - 32);
    const center = rect.left + rect.width / 2;
    const left = Math.max(16, Math.min(center - width / 2, vw - 16 - width));
    // Below the control unless it sits in the bottom part of the screen, or the
    // callout asks for above (and there is room there).
    const above = rect.bottom > vh - 180 || (TIPS[tip!].prefer === "above" && rect.top > 140);
    bubble = {
      left,
      ...(above ? { bottom: vh - rect.top + GAP } : { top: rect.bottom + GAP }),
      arrowLeft: Math.max(16, Math.min(center - left, width - 16)),
      above,
    };
  }

  return createPortal(
    <AnimatePresence>
      {show && rect && bubble && tip && (
        <motion.div
          key={tip}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0.1 : 0.18 }}
          className="fixed inset-0 z-[70] pointer-events-none"
          data-testid={`first-order-tip-${tip}`}
        >
          <div
            className="absolute rounded-xl ring-4 ring-[#F2B84B] ring-offset-2 ring-offset-transparent"
            style={{ left: rect.left - 3, top: rect.top - 3, width: rect.width + 6, height: rect.height + 6 }}
          />
          <motion.div
            role="status"
            initial={reduceMotion ? false : { y: bubble.above ? 6 : -6 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute pointer-events-auto rounded-xl bg-gray-900 text-[#fff] shadow-xl pl-4 pr-3 py-2.5 flex items-center gap-3"
            style={{ left: bubble.left, top: bubble.top, bottom: bubble.bottom, width: Math.min(BUBBLE_MAX_W, window.innerWidth - 32) }}
          >
            <span
              className="absolute h-3 w-3 rotate-45 bg-gray-900"
              style={{ left: bubble.arrowLeft - 6, ...(bubble.above ? { bottom: -6 } : { top: -6 }) }}
            />
            <p className="flex-1 text-[15px] leading-snug">{TIPS[tip].text(context)}</p>
            <button
              type="button"
              onClick={() => onDismiss(tip)}
              className="shrink-0 min-h-[40px] px-2 text-[15px] font-semibold text-[#F2B84B] whitespace-nowrap"
              data-testid="first-order-tip-dismiss"
            >
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
